import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import { get } from 'lodash'
import Fuse from 'fuse.js'
import { ChevronUp, ChevronDown, Plus } from 'lucide-react'

import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import AddCoachModal from '../leevo_ui/AddCoachModal/AddCoachModal'
import Notification from '../leevo_ui/Notification/Notification'
import StaffDrawer from '../leevo_ui/StaffDrawer/StaffDrawer'
import Avatar from '../leevo_ui/Avatar/Avatar'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  ScrollableTableContainer,
  ScrollableTable,
  ScrollableTableHeader,
  ScrollableTableBody,
  ScrollableTableHead,
  ScrollableTableRow,
  ScrollableTableCell
} from '../ui/scrollable-table'
import { requestCreator } from '../../utilities/requests'
import useFacility from '../../utilities/hooks/useFacility'
import useAuth from '../../utilities/hooks/useAuth'

const { get: getCoaches, cancel: cancelCoachesRequest } = requestCreator()

const fuzzySearchOptions = {
  findAllMatches: true,
  keys: ['fullName', 'preferredName', 'email']
}

function FacilitiesCoachesIndex() {
  const { facility, isLoading: isFacilityLoading } = useFacility({
    isSubscriptionRequired: false
  })
  const history = useHistory()
  const [query, setQuery] = useState('')
  const [coaches, setCoaches] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAddingUser, setIsAddingUser] = useState(false)
  const [notification, setNotification] = useState({ show: false, message: '', color: 'green' })
  const [selectedStaffId, setSelectedStaffId] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)
  const { isSignedIn, isLoading: isAuthLoading } = useAuth()

  useEffect(() => {
    if (isFacilityLoading || isAuthLoading || !facility?.id) return

    setIsLoading(true)
    getCoaches(`/api/facilities/${facility.id}/coaches`)
      .then((nextCoaches) => setCoaches(nextCoaches))
      .catch((error) => {
        if (!axios.isCancel(error)) {
          if (get(error, 'response.status') === 401 && !isSignedIn) {
            history.replace('/login')
          } else {
            history.replace('/500')
          }
        }
      })
      .finally(() => {
        setIsLoading(false)
      })

    return cancelCoachesRequest
  }, [facility?.id, history, isAuthLoading, isFacilityLoading, isSignedIn])

  function isPending(coach) {
    return coach.invitedToSignUp && !coach.invitationAccepted
  }

  const filteredCoaches = useMemo(() => {
    if (!coaches) return []
    if (!query) return coaches

    const fuzzySearch = new Fuse(coaches, fuzzySearchOptions)
    return fuzzySearch.search(query).map(({ item }) => item)
  }, [coaches, query])

  function getBadgeVariant(permissionLevel) {
    switch (permissionLevel) {
      case 'director':
        return 'default'
      case 'coach':
        return 'secondary'
      case 'staff':
        return 'outline'
      default:
        return 'outline'
    }
  }

  function getPermissionLabel(permissionLevel) {
    switch (permissionLevel) {
      case 'director':
        return 'Director'
      case 'coach':
        return 'Coach'
      case 'staff':
        return 'Staff'
      default:
        return 'Unknown'
    }
  }

  function handleRowClick(staffId) {
    setSelectedStaffId(staffId)
    setIsDrawerOpen(true)
  }

  function handleDrawerClose() {
    setIsDrawerOpen(false)
    setSelectedStaffId(null)
  }

  function handleStaffUpdate(updatedStaff) {
    setCoaches((prevCoaches) =>
      prevCoaches.map((coach) => (coach.id === updatedStaff.id ? { ...coach, ...updatedStaff.attributes } : coach))
    )
    setNotification({
      show: true,
      message: 'Staff member updated successfully',
      color: 'green'
    })
  }

  function handleStaffDelete(deletedStaffId) {
    setCoaches((prevCoaches) => prevCoaches.filter((coach) => String(coach.id) !== String(deletedStaffId)))
    setNotification({
      show: true,
      message: 'Staff member removed from facility',
      color: 'green'
    })
  }

  function handleStaffDeleteError(message) {
    setNotification({
      show: true,
      message,
      color: 'red'
    })
  }

  function handlePromoteClick(coach, e) {
    e.stopPropagation()
    const newLevel = getPermissionLevelFromFlags(getPromotedPermissions(coach.permissionLevel))
    setConfirmAction({
      type: 'promote',
      coach,
      newLevel,
      message: `Are you sure you want to promote ${coach.preferredName || coach.fullName} to ${getPermissionLabel(
        newLevel
      )}?`
    })
  }

  function handleDemoteClick(coach, e) {
    e.stopPropagation()
    const newLevel = getPermissionLevelFromFlags(getDemotedPermissions(coach.permissionLevel))
    setConfirmAction({
      type: 'demote',
      coach,
      newLevel,
      message: `Are you sure you want to demote ${coach.preferredName || coach.fullName} to ${getPermissionLabel(
        newLevel
      )}?`
    })
  }

  async function confirmPermissionChange() {
    if (!confirmAction) return

    const { coach, type } = confirmAction
    const newPermissions =
      type === 'promote' ? getPromotedPermissions(coach.permissionLevel) : getDemotedPermissions(coach.permissionLevel)

    try {
      await axios.patch(`/api/facilities/${facility.id}/coaches/${coach.id}`, {
        coach: newPermissions
      })
      setCoaches((prevCoaches) =>
        prevCoaches.map((c) =>
          c.id === coach.id ? { ...c, permissionLevel: getPermissionLevelFromFlags(newPermissions) } : c
        )
      )
      setNotification({
        show: true,
        message: `${type === 'promote' ? 'Promoted' : 'Demoted'} to ${getPermissionLabel(
          getPermissionLevelFromFlags(newPermissions)
        )}`
      })
      setConfirmAction(null)
    } catch (err) {
      setNotification({
        show: true,
        message: 'Failed to update permissions'
      })
      setConfirmAction(null)
    }
  }

  function getPromotedPermissions(currentLevel) {
    switch (currentLevel) {
      case 'staff':
        return { admin: false, coach: true }
      case 'coach':
        return { admin: true, coach: true }
      default:
        return { admin: true, coach: true }
    }
  }

  function getDemotedPermissions(currentLevel) {
    switch (currentLevel) {
      case 'director':
        return { admin: false, coach: true }
      case 'coach':
        return { admin: false, coach: false }
      default:
        return { admin: false, coach: false }
    }
  }

  function getPermissionLevelFromFlags(flags) {
    if (flags.admin) return 'director'
    if (flags.coach) return 'coach'
    return 'staff'
  }

  function canPromote(permissionLevel) {
    return permissionLevel !== 'director'
  }

  function canDemote(permissionLevel) {
    return permissionLevel !== 'staff'
  }

  return (
    <ApplicationLayout data-cy="staff-index">
      <AddCoachModal
        open={isAddingUser}
        onClose={({ invitedUsers } = {}) => {
          setIsAddingUser(false)
          if (invitedUsers) {
            setNotification({
              show: true,
              message: 'Successfully added staff members'
            })
            setCoaches([...coaches, ...invitedUsers])
          }
        }}
      />
      <StaffDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        staffId={selectedStaffId}
        facilityId={facility?.id}
        onUpdate={handleStaffUpdate}
        onDelete={handleStaffDelete}
        onActionError={handleStaffDeleteError}
      />

      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-blue-100">
                {confirmAction.type === 'promote' ? (
                  <ChevronUp className="w-8 h-8 text-blue-600" />
                ) : (
                  <ChevronDown className="w-8 h-8 text-blue-600" />
                )}
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {confirmAction.type === 'promote' ? 'Promote Staff Member?' : 'Demote Staff Member?'}
              </h3>
              <p className="text-sm text-gray-500 mb-6">{confirmAction.message}</p>
              <div className="flex gap-3 w-full">
                <Button variant="outline" onClick={() => setConfirmAction(null)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={confirmPermissionChange} className="flex-1">
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Notification
        show={notification.show}
        color={notification.color}
        onDisappearTimeout={() => setNotification({ ...notification, show: false })}
      >
        {notification.message}
      </Notification>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 pt-6">
          <h1 className="text-2xl font-semibold text-gray-900">Staff</h1>
          <Button onClick={() => setIsAddingUser(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="h-4 w-4 mr-1" />
            Add Staff
          </Button>
        </div>
      </div>
      {isLoading ? (
        <LoadingSpinner size="1/5" className="flex items-center justify-center m-10" />
      ) : (
        <div className="flex justify-center h-auto px-4 py-6">
          <div className="w-full max-w-7xl">
            <div className="mb-6">
              <div className="relative max-w-xl">
                <input
                  id="search"
                  type="text"
                  className="block w-full pl-9 pr-12 py-3 text-base text-gray-900 placeholder-gray-500 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent shadow-sm transition-all h-12"
                  placeholder="Search coaches by name or email..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {query && (
                <p className="mt-2 text-sm text-gray-600">
                  {filteredCoaches.length} {filteredCoaches.length === 1 ? 'result' : 'results'} found
                </p>
              )}
            </div>
            <ScrollableTableContainer className="hidden lg:block" height={650}>
              <ScrollableTable>
                <ScrollableTableHeader>
                  <ScrollableTableRow>
                    <ScrollableTableHead>Staff Member</ScrollableTableHead>
                    <ScrollableTableHead>Email</ScrollableTableHead>
                    <ScrollableTableHead>Permission Level</ScrollableTableHead>
                    <ScrollableTableHead>Status</ScrollableTableHead>
                    <ScrollableTableHead className="text-right">Actions</ScrollableTableHead>
                  </ScrollableTableRow>
                </ScrollableTableHeader>
                <ScrollableTableBody>
                  {filteredCoaches.map((coach) => (
                    <ScrollableTableRow
                      key={coach.id}
                      onClick={() => handleRowClick(coach.id)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <ScrollableTableCell>
                        <div className="flex items-center gap-3">
                          <Avatar
                            fullName={coach.fullName || coach.preferredName}
                            cloudinaryPhotoPublicId={coach.cloudinaryPhotoPublicId}
                            userId={coach.id}
                            size={10}
                          />
                          <div>
                            <p className="font-semibold text-gray-900">
                              {coach.preferredName || coach.fullName || 'No name'}
                            </p>
                            {coach.preferredName && coach.fullName !== coach.preferredName && (
                              <p className="text-sm text-gray-500">{coach.fullName}</p>
                            )}
                          </div>
                        </div>
                      </ScrollableTableCell>
                      <ScrollableTableCell>
                        <span className="text-gray-700">{coach.email}</span>
                      </ScrollableTableCell>
                      <ScrollableTableCell>
                        <Badge variant={getBadgeVariant(coach.permissionLevel)}>
                          {getPermissionLabel(coach.permissionLevel)}
                        </Badge>
                      </ScrollableTableCell>
                      <ScrollableTableCell>
                        {isPending(coach) ? (
                          <Badge variant="outline" className="text-yellow-700 border-yellow-300 bg-yellow-50">
                            Pending
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">
                            Active
                          </Badge>
                        )}
                      </ScrollableTableCell>
                      <ScrollableTableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRowClick(coach.id)
                            }}
                            className="h-8"
                          >
                            Edit
                          </Button>
                          {canPromote(coach.permissionLevel) && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={(e) => handlePromoteClick(coach, e)}
                              className="h-8 text-white"
                            >
                              Promote
                            </Button>
                          )}
                          {canDemote(coach.permissionLevel) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleDemoteClick(coach, e)}
                              className="h-8 bg-red-500 text-white"
                            >
                              Demote
                            </Button>
                          )}
                        </div>
                      </ScrollableTableCell>
                    </ScrollableTableRow>
                  ))}
                  {filteredCoaches.length === 0 && (
                    <ScrollableTableRow>
                      <ScrollableTableCell colSpan={5} className="text-center py-8 text-gray-500">
                        {query ? 'No staff members found matching your search.' : 'No staff members yet.'}
                      </ScrollableTableCell>
                    </ScrollableTableRow>
                  )}
                </ScrollableTableBody>
              </ScrollableTable>
            </ScrollableTableContainer>

            <div className="space-y-3 lg:hidden">
              {filteredCoaches.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500 bg-white border border-gray-200 rounded-lg">
                  {query ? 'No staff members found matching your search.' : 'No staff members yet.'}
                </div>
              ) : (
                filteredCoaches.map((coach) => (
                  <div
                    key={coach.id}
                    onClick={() => handleRowClick(coach.id)}
                    className="p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar
                        fullName={coach.fullName || coach.preferredName}
                        cloudinaryPhotoPublicId={coach.cloudinaryPhotoPublicId}
                        userId={coach.id}
                        size={12}
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {coach.preferredName || coach.fullName || 'No name'}
                        </p>
                        {coach.preferredName && coach.fullName !== coach.preferredName && (
                          <p className="text-sm text-gray-500">{coach.fullName}</p>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Email</label>
                      <p className="text-sm text-gray-900">{coach.email}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Permission</label>
                        <Badge variant={getBadgeVariant(coach.permissionLevel)}>
                          {getPermissionLabel(coach.permissionLevel)}
                        </Badge>
                      </div>
                      <div>
                        <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Status</label>
                        {isPending(coach) ? (
                          <Badge variant="outline" className="text-yellow-700 border-yellow-300 bg-yellow-50">
                            Pending
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">
                            Active
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 pt-3 mt-3 border-t border-gray-200">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRowClick(coach.id)
                        }}
                        className="w-full h-9"
                      >
                        Edit
                      </Button>
                      <div className="flex gap-2">
                        {canPromote(coach.permissionLevel) && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={(e) => handlePromoteClick(coach, e)}
                            className="flex-1 h-9 text-white"
                          >
                            Promote
                          </Button>
                        )}
                        {canDemote(coach.permissionLevel) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleDemoteClick(coach, e)}
                            className="flex-1 h-9 bg-red-500 text-white"
                          >
                            Demote
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </ApplicationLayout>
  )
}

export default FacilitiesCoachesIndex
